'use client';

import {
  Component,
  useCallback,
  useState,
  useTransition,
  type ReactNode,
} from 'react';
import { Eye, Plus, RotateCcw, Save, User } from 'lucide-react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@kit/ui/card';
import { Form } from '@kit/ui/form';
import { Separator } from '@kit/ui/separator';
import { toast } from '@kit/ui/sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@kit/ui/tabs';
import { Trans } from '@kit/ui/trans';

import {
  applyApplicationTemplateAction,
  updateApplicationStructureAction,
} from '../../server/actions/form-builder.actions';
import type {
  ApplicationFieldConfig,
  ApplicationStructure,
  ApplicationTemplateId,
} from '../../types/audition.types';
import { DynamicFormRenderer } from './dynamic-form-renderer';
import { FieldConfigEditor } from './field-config-editor';
import { FormBuilderPanel } from './form-builder-panel';
import { TemplateSelector } from './template-selector';

// Pre-defined profile fields palette — mirrors Meteor's "Profile fields" source panel.
// These are fields that map to professional_profiles columns.
export const PROFILE_FIELDS: Readonly<
  Omit<ApplicationFieldConfig, 'sortOrder'>[]
> = [
  {
    key: 'firstname',
    type: 'text',
    label: 'First Name',
    placeholder: 'First name',
    validation: { required: true, maxLength: 100 },
    width: 'half',
  },
  {
    key: 'lastname',
    type: 'text',
    label: 'Last Name',
    placeholder: 'Last name',
    validation: { required: true, maxLength: 100 },
    width: 'half',
  },
  {
    key: 'contactEmail',
    type: 'email',
    label: 'Contact Email',
    placeholder: 'your@email.com',
    validation: { required: true },
    width: 'full',
  },
  {
    key: 'contactPhone',
    type: 'phone',
    label: 'Phone Number',
    placeholder: '+1 (555) 000-0000',
    width: 'half',
  },
  {
    key: 'birthdate',
    type: 'date',
    label: 'Date of Birth',
    validation: { required: true },
    width: 'half',
  },
  {
    key: 'gender',
    type: 'radio',
    label: 'Gender',
    options: [
      { value: 'male', label: 'Male' },
      { value: 'female', label: 'Female' },
      { value: 'nonBinary', label: 'Non-binary' },
    ],
    width: 'half',
  },
  {
    key: 'height',
    type: 'number',
    label: 'Height (cm)',
    validation: { min: 60, max: 250 },
    width: 'half',
  },
  {
    key: 'nationalities',
    type: 'multiselect',
    label: 'Nationalities',
    width: 'half',
  },
  {
    key: 'residenceCountry',
    type: 'select',
    label: 'Country of Residence',
    width: 'half',
  },
  {
    key: 'city',
    type: 'text',
    label: 'City',
    placeholder: 'Berlin',
    width: 'half',
  },
  {
    key: 'languages',
    type: 'multiselect',
    label: 'Languages',
    width: 'half',
  },
  {
    key: 'eyeColor',
    type: 'text',
    label: 'Eye Color',
    placeholder: 'Brown',
    width: 'half',
  },
  {
    key: 'hairColor',
    type: 'text',
    label: 'Hair Color',
    placeholder: 'Black',
    width: 'half',
  },
  {
    key: 'skinTone',
    type: 'text',
    label: 'Skin Tone',
    placeholder: 'Olive',
    width: 'half',
  },
  {
    key: 'weight',
    type: 'number',
    label: 'Weight (kg)',
    validation: { min: 25, max: 200 },
    width: 'half',
  },
  {
    key: 'bio',
    type: 'textarea',
    label: 'Bio',
    description:
      'Short paragraph about yourself, your training, and your style.',
    placeholder: 'Tell us about your dance journey...',
    validation: { maxLength: 2000 },
    width: 'full',
  },
  {
    key: 'yearsOfExperience',
    type: 'number',
    label: 'Years of Experience',
    validation: { min: 0, max: 80 },
    width: 'half',
  },
  {
    key: 'danceStyles',
    type: 'multiselect',
    label: 'Dance Styles',
    width: 'half',
  },
  {
    key: 'portfolioUrl',
    type: 'text',
    label: 'Portfolio URL',
    description: 'Personal site, Linktree, or reel collection.',
    placeholder: 'https://...',
    width: 'full',
  },
  {
    key: 'stageName',
    type: 'text',
    label: 'Stage Name',
    placeholder: 'Preferred / stage name',
    validation: { maxLength: 255 },
    width: 'half',
  },
  {
    key: 'website',
    type: 'url',
    label: 'Website',
    placeholder: 'https://...',
    validation: { maxLength: 500 },
    width: 'half',
  },
  {
    key: 'instagramUsername',
    type: 'text',
    label: 'Instagram Username',
    placeholder: '@username',
    width: 'half',
  },
  {
    key: 'tiktokUsername',
    type: 'text',
    label: 'TikTok Username',
    placeholder: '@username',
    width: 'half',
  },
  {
    key: 'headshot',
    type: 'file',
    label: 'Photo / Headshot',
    description: 'Recent photo or headshot (JPG or PNG, max 5MB)',
    validation: {
      fileTypes: ['image/jpeg', 'image/png'],
      maxFileSize: 5 * 1024 * 1024,
    },
    width: 'full',
  },
  {
    key: 'videoLink',
    type: 'video',
    label: 'Audition Video',
    description: 'Link to your audition video (YouTube, Vimeo, etc.)',
    width: 'full',
  },
  {
    key: 'resume',
    type: 'file',
    label: 'Dance Resume / CV',
    description: 'Your professional CV (PDF, max 5MB)',
    validation: {
      fileTypes: ['application/pdf'],
      maxFileSize: 5 * 1024 * 1024,
    },
    width: 'full',
  },
] as const;

interface ApplicationFormBuilderProps {
  auditionId: string;
  auditionTitle: string;
  initialStructure: ApplicationStructure;
}

type BuilderTab = 'build' | 'preview' | 'template';

/**
 * Main application form builder component.
 * Combines profile field palette, form canvas, and field configuration editor.
 * Uses native HTML5 drag-and-drop for reordering within the canvas.
 */
export function ApplicationFormBuilder({
  auditionId,
  auditionTitle,
  initialStructure,
}: ApplicationFormBuilderProps) {
  const { t } = useTranslation();
  const [structure, setStructure] =
    useState<ApplicationStructure>(initialStructure);
  const [selectedField, setSelectedField] =
    useState<ApplicationFieldConfig | null>(null);
  const [activeTab, setActiveTab] = useState<BuilderTab>('build');
  const [hasChanges, setHasChanges] = useState(false);
  const [isPending, startTransition] = useTransition();

  // The preview tab's react-hook-form instance is created here at the
  // component root rather than inside <PreviewForm>. Radix Tabs unmounts
  // inactive TabsContent by default; with React 19 concurrent rendering,
  // unmounting <PreviewForm> while a child <Controller> was mid-render
  // produced "Cannot read properties of undefined (reading 'array')" via
  // useController.ts:64 (control._names.array). Sentry #7453626758.
  // Lifting useForm here keeps the form alive across tab switches so the
  // race window closes.
  const previewForm = useForm<Record<string, unknown>>({ defaultValues: {} });

  const handleStructureChange = useCallback((updated: ApplicationStructure) => {
    setStructure(updated);
    setHasChanges(true);
  }, []);

  const handleFieldSelect = useCallback(
    (field: ApplicationFieldConfig | null) => {
      setSelectedField(field);
    },
    [],
  );

  const handleFieldUpdate = useCallback(
    (updatedField: ApplicationFieldConfig) => {
      setStructure((prev) => {
        const updatedFields = prev.fields.map((f) =>
          f.key === updatedField.key ? updatedField : f,
        );
        return { ...prev, fields: updatedFields };
      });
      setSelectedField(updatedField);
      setHasChanges(true);
    },
    [],
  );

  // Add a profile field to the form. Only adds if the key is not already present.
  const handleAddProfileField = useCallback(
    (template: Omit<ApplicationFieldConfig, 'sortOrder'>) => {
      setStructure((prev) => {
        const alreadyExists = prev.fields.some((f) => f.key === template.key);
        if (alreadyExists) {
          toast.info(t('auditions:formBuilder.toast.fieldAlreadyInForm'), {
            description: t('auditions:formBuilder.toast.fieldAlreadyAdded', {
              label: template.label,
            }),
          });
          return prev;
        }

        const maxSortOrder = Math.max(
          0,
          ...prev.fields.map((f) => f.sortOrder),
        );
        const newField: ApplicationFieldConfig = {
          ...template,
          // Palette fields are profile-bound: prefill from the dancer profile
          // on apply, fill-if-null backfill on submit (PROFILE_FIELD_MAP).
          source: 'profile',
          sortOrder: maxSortOrder + 1,
        };

        return { ...prev, fields: [...prev.fields, newField] };
      });
      setHasChanges(true);
    },
    [t],
  );

  const handleSave = useCallback(() => {
    startTransition(async () => {
      const result = await updateApplicationStructureAction({
        auditionId,
        applicationStructure: structure,
      });

      if (result.success) {
        setHasChanges(false);
        toast.success(t('auditions:formBuilder.toast.formSaved'), {
          description: t('auditions:formBuilder.toast.formSavedDescription'),
        });
      } else {
        toast.error(t('auditions:formBuilder.toast.saveFailed'), {
          description:
            typeof result.error === 'string'
              ? result.error
              : t('auditions:formBuilder.toast.saveError'),
        });
      }
    });
  }, [auditionId, structure, t]);

  const handleRestoreDefault = useCallback(() => {
    startTransition(async () => {
      const result = await applyApplicationTemplateAction({
        auditionId,
        templateId: 'basic',
      });

      if (result.success) {
        window.location.reload();
      } else {
        toast.error(t('auditions:formBuilder.toast.restoreFailed'), {
          description:
            typeof result.error === 'string'
              ? result.error
              : t('auditions:formBuilder.toast.genericError'),
        });
      }
    });
  }, [auditionId, t]);

  const handleTemplateSelect = useCallback(
    (templateId: ApplicationTemplateId) => {
      startTransition(async () => {
        const result = await applyApplicationTemplateAction({
          auditionId,
          templateId,
        });

        if (result.success) {
          window.location.reload();
        } else {
          toast.error(t('auditions:formBuilder.toast.applyTemplateFailed'), {
            description:
              typeof result.error === 'string'
                ? result.error
                : t('auditions:formBuilder.toast.genericError'),
          });
        }
      });
    },
    [auditionId, t],
  );

  const existingKeys = structure.fields.map((f) => f.key);

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">
            <Trans i18nKey="auditions:formBuilder.title" />
          </h2>
          <p className="text-muted-foreground text-sm">
            <Trans
              i18nKey="auditions:formBuilder.subtitle"
              values={{ title: auditionTitle }}
            />
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRestoreDefault}
            disabled={isPending}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            <Trans i18nKey="auditions:formBuilder.restoreDefaults" />
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || isPending}
          >
            <Save className="mr-2 h-4 w-4" />
            {isPending ? (
              <Trans i18nKey="auditions:formBuilder.saving" />
            ) : (
              <Trans i18nKey="auditions:formBuilder.saveForm" />
            )}
          </Button>
        </div>
      </div>

      <Separator />

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as BuilderTab)}
      >
        <TabsList>
          <TabsTrigger value="build">
            <Trans i18nKey="auditions:formBuilder.tabBuilder" />
          </TabsTrigger>
          <TabsTrigger value="preview">
            <Eye className="mr-2 h-4 w-4" />
            <Trans i18nKey="auditions:formBuilder.tabPreview" />
          </TabsTrigger>
          <TabsTrigger value="template">
            <Trans i18nKey="auditions:formBuilder.tabTemplates" />
          </TabsTrigger>
        </TabsList>

        {/* Builder tab — profile palette + canvas + field editor */}
        <TabsContent value="build" className="mt-4">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
            {/* Left: Profile fields palette */}
            <div className="lg:col-span-1">
              <ProfileFieldsPalette
                existingKeys={existingKeys}
                onAdd={handleAddProfileField}
              />
            </div>

            {/* Center: Form canvas */}
            <div className="lg:col-span-2">
              <FormBuilderPanel
                structure={structure}
                onChange={handleStructureChange}
                onFieldSelect={handleFieldSelect}
                selectedFieldKey={selectedField?.key}
              />
            </div>

            {/* Right: Field config editor */}
            <div className="lg:col-span-1">
              {selectedField ? (
                <FieldConfigEditor
                  field={selectedField}
                  structure={structure}
                  onChange={handleFieldUpdate}
                />
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      <Trans i18nKey="auditions:formBuilder.fieldSettings" />
                    </CardTitle>
                    <CardDescription>
                      <Trans i18nKey="auditions:formBuilder.fieldSettingsHint" />
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-lg border-2 border-dashed p-8 text-center">
                      <p className="text-muted-foreground text-sm">
                        <Trans i18nKey="auditions:formBuilder.clickFieldHint" />
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Preview tab — renders the form as applicants see it */}
        <TabsContent value="preview" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>
                <Trans i18nKey="auditions:formBuilder.formPreview" />
              </CardTitle>
              <CardDescription>
                <Trans i18nKey="auditions:formBuilder.formPreviewHint" />
              </CardDescription>
            </CardHeader>
            <CardContent>
              {structure.fields.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed p-8 text-center">
                  <p className="text-muted-foreground text-sm">
                    <Trans i18nKey="auditions:formBuilder.previewNoFields" />
                  </p>
                </div>
              ) : (
                <PreviewErrorBoundary
                  fallback={
                    <div className="rounded-lg border-2 border-dashed p-8 text-center">
                      <p className="text-muted-foreground text-sm">
                        <Trans i18nKey="auditions:formBuilder.previewUnavailable" />
                      </p>
                    </div>
                  }
                >
                  <PreviewForm form={previewForm} structure={structure} />
                </PreviewErrorBoundary>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates tab */}
        <TabsContent value="template" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>
                <Trans i18nKey="auditions:formBuilder.startFromTemplate" />
              </CardTitle>
              <CardDescription>
                <Trans i18nKey="auditions:formBuilder.startFromTemplateHint" />
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TemplateSelector onSelect={handleTemplateSelect} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ==================== Sub-components ====================

interface ProfileFieldsPaletteProps {
  existingKeys: string[];
  onAdd: (field: Omit<ApplicationFieldConfig, 'sortOrder'>) => void;
}

/**
 * Palette of pre-configured profile fields — matches the Meteor
 * "Profile fields" source panel. Fields already in the form are
 * shown with a checkmark.
 */
function ProfileFieldsPalette({
  existingKeys,
  onAdd,
}: ProfileFieldsPaletteProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <User className="text-muted-foreground h-4 w-4" />
          <CardTitle className="text-base">
            <Trans i18nKey="auditions:formBuilder.profileFields" />
          </CardTitle>
        </div>
        <CardDescription className="text-xs">
          <Trans i18nKey="auditions:formBuilder.profileFieldsHint" />
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-1 px-3 pb-3">
        {PROFILE_FIELDS.map((field) => {
          const isAdded = existingKeys.includes(field.key);
          return (
            <Button
              key={field.key}
              type="button"
              variant="ghost"
              disabled={isAdded}
              onClick={() => onAdd(field)}
              className={
                'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm transition-colors ' +
                (isAdded
                  ? 'text-muted-foreground cursor-default opacity-50'
                  : 'hover:bg-muted cursor-pointer')
              }
            >
              <span className="truncate">{field.label}</span>
              {isAdded ? (
                <Badge variant="secondary" className="ml-2 shrink-0 text-xs">
                  <Trans i18nKey="auditions:formBuilder.added" />
                </Badge>
              ) : (
                <Plus className="ml-2 h-3 w-3 shrink-0 opacity-60" />
              )}
            </Button>
          );
        })}
      </CardContent>
    </Card>
  );
}

/**
 * Catches render-time crashes inside the preview tree (e.g. the
 * useController.ts:64 race documented in Sentry #7453626758) so the whole
 * /applications/edit route does not unmount on a stale form-control read.
 * Inlined to avoid pulling @kit/monitoring into this package's dep graph.
 */
class PreviewErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch() {
    // Sentry already captures via global handler; no extra report needed.
  }
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

/**
 * Preview wrapper that renders the form without allowing submission.
 * The form instance is created by the parent <ApplicationFormBuilder> so
 * it survives Radix Tabs unmount/remount cycles (see Sentry #7453626758
 * for the race condition this avoids).
 */
function PreviewForm({
  form,
  structure,
}: {
  form: UseFormReturn<Record<string, unknown>>;
  structure: ApplicationStructure;
}) {
  return (
    <Form {...form}>
      <fieldset disabled className="space-y-6">
        <DynamicFormRenderer structure={structure} form={form} />
      </fieldset>
    </Form>
  );
}
